import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from './useAuth'

// ── EVENTS (This Week "Coming up") ──────────────────────────────────────
// Church-scoped events with an "I'm going" RSVP toggle, read from the
// unified `church_events` table (events/event_attendees unification —
// see supabase-schema-events-unify.sql). Recurring events are stored as a
// single row and expanded into their occurrences within a date window
// client-side — simplest correct approach without a server-side job.
// `event_exceptions` lets an elder/admin cancel or override a single
// occurrence without touching the series.

const DAY_MS = 24 * 60 * 60 * 1000

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function clampDay(year, month, day) {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(day, lastDay))
}

// Which "Nth weekday of month" a date represents, e.g. the 3rd Tuesday -> 3.
function weekdayOrdinal(date) {
  return Math.ceil(date.getDate() / 7)
}

function nthWeekdayOfMonth(year, month, weekday, n) {
  const first = new Date(year, month, 1)
  const firstWeekday = first.getDay()
  const day = 1 + ((weekday - firstWeekday + 7) % 7) + (n - 1) * 7
  const lastDay = new Date(year, month + 1, 0).getDate()
  if (day > lastDay) return null
  return new Date(year, month, day)
}

// Expand one church_events row into every occurrence between windowStart
// and windowEnd (inclusive), respecting repeat_until and all 5 recurrence
// rules. Returns Date objects at local midnight.
function expandOccurrences(ev, windowStart, windowEnd) {
  // event_date/repeat_until are `date` columns, so Postgrest should always
  // hand back a plain "YYYY-MM-DD" — but if either column ever drifted to a
  // timestamp type (e.g. a table that predates supabase-schema-events-
  // unify.sql's `create table if not exists`, which is a no-op against an
  // existing table), Postgrest returns a full ISO string instead, and
  // appending "T00:00:00" to THAT ("...T00:00:00+00:00T00:00:00") is an
  // invalid Date that silently disappears from every window check below.
  // Slicing to the first 10 chars gets the date part either way.
  const base = new Date(`${String(ev.event_date || '').slice(0, 10)}T00:00:00`)
  if (Number.isNaN(base.getTime())) return []
  const until = ev.repeat_until ? new Date(`${String(ev.repeat_until).slice(0, 10)}T23:59:59`) : null
  const withinUntil = (d) => !until || d <= until

  if (ev.recurrence === 'none' || !ev.recurrence) {
    return (base >= windowStart && base <= windowEnd && withinUntil(base)) ? [base] : []
  }

  const out = []

  if (ev.recurrence === 'weekly' || ev.recurrence === 'fortnightly') {
    const stepDays = ev.recurrence === 'fortnightly' ? 14 : 7
    let d = new Date(base)
    if (d < windowStart) {
      const missed = Math.ceil((windowStart - d) / (stepDays * DAY_MS))
      d = new Date(d.getTime() + missed * stepDays * DAY_MS)
    }
    while (d <= windowEnd) {
      if (d >= windowStart && withinUntil(d)) out.push(new Date(d))
      d = new Date(d.getTime() + stepDays * DAY_MS)
    }
    return out
  }

  if (ev.recurrence === 'monthly_date') {
    const day = base.getDate()
    for (let monthsAhead = 0; monthsAhead < 36; monthsAhead++) {
      const monthStart = new Date(base.getFullYear(), base.getMonth() + monthsAhead, 1)
      if (monthStart > windowEnd) break
      const occ = clampDay(base.getFullYear(), base.getMonth() + monthsAhead, day)
      if (occ >= windowStart && occ <= windowEnd && withinUntil(occ)) out.push(occ)
    }
    return out
  }

  if (ev.recurrence === 'monthly_weekday') {
    const weekday = base.getDay()
    const n = weekdayOrdinal(base)
    for (let monthsAhead = 0; monthsAhead < 36; monthsAhead++) {
      const monthStart = new Date(base.getFullYear(), base.getMonth() + monthsAhead, 1)
      if (monthStart > windowEnd) break
      const occ = nthWeekdayOfMonth(base.getFullYear(), base.getMonth() + monthsAhead, weekday, n)
      if (occ && occ >= windowStart && occ <= windowEnd && withinUntil(occ)) out.push(occ)
    }
    return out
  }

  return []
}

export function useEvents(churchIdOverride) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  // Surfaced in the UI (ThisWeekTab) rather than just console.error'd, so a
  // read failure (RLS, a broken embedded-relationship join, etc.) reads as
  // an actual error instead of "events just aren't showing" with no clue why.
  const [error, setError] = useState('')
  const { user, profile } = useAuth()
  const churchId = churchIdOverride || profile?.church_id

  const fetch = useCallback(async () => {
    if (!churchId) { setEvents([]); setLoading(false); return }
    setLoading(true)
    const now = new Date(); now.setHours(0, 0, 0, 0)
    const weekAhead = new Date(now.getTime() + 7 * DAY_MS)

    // church_events and event_attendees are fetched as two plain queries
    // rather than one embedded `church_events(..., event_attendees(...))`
    // select. PostgREST's embedded-resource syntax depends on it having the
    // event_attendees -> church_events foreign key in its schema cache,
    // which has proven unreliable to force a reload of on some Supabase
    // instances (a manual `notify pgrst, 'reload schema'` doesn't always
    // take effect, e.g. behind certain poolers) — "Could not find a
    // relationship" even once the FK genuinely exists. Two separate queries
    // sidestep relationship inference entirely; only event_attendees ->
    // profiles is still embedded, since that one hasn't been reported broken.
    const [evRes, exRes] = await Promise.all([
      supabase.from('church_events')
        .select('*')
        .eq('church_id', churchId)
        .order('event_date', { ascending: true }),
      supabase.from('event_exceptions')
        .select('event_id, occurrence_date, cancelled, override_time, override_location, override_title')
        .gte('occurrence_date', ymd(now)).lte('occurrence_date', ymd(weekAhead)),
    ])
    if (evRes.error) {
      console.error('useEvents load:', evRes.error)
      setEvents([])
      setError(evRes.error.message || 'Could not load events.')
      setLoading(false)
      return
    }
    setError('')

    const eventIds = (evRes.data || []).map(ev => ev.id)
    let attendeesByEventId = new Map()
    if (eventIds.length > 0) {
      const { data: attendeeRows, error: attErr } = await supabase
        .from('event_attendees')
        .select('event_id, user_id, occurrence_date, profiles(full_name, avatar_url)')
        .in('event_id', eventIds)
      if (attErr) {
        // Non-fatal — still show the events themselves, just without who's going.
        console.warn('useEvents attendees load:', attErr.message)
      } else {
        for (const a of (attendeeRows || [])) {
          if (!attendeesByEventId.has(a.event_id)) attendeesByEventId.set(a.event_id, [])
          attendeesByEventId.get(a.event_id).push(a)
        }
      }
    }

    const exceptionsByKey = new Map()
    for (const ex of (exRes.data || [])) exceptionsByKey.set(`${ex.event_id}:${ex.occurrence_date}`, ex)

    const rows = (evRes.data || []).flatMap(ev => {
      const attendees = attendeesByEventId.get(ev.id) || []
      const occurrences = expandOccurrences(ev, now, weekAhead)
      return occurrences.map(date => {
        const dateStr = ymd(date)
        const exception = exceptionsByKey.get(`${ev.id}:${dateStr}`)
        if (exception?.cancelled) return null
        const occAttendees = attendees.filter(a => a.occurrence_date === dateStr)
        const attendeeIds = occAttendees.map(a => a.user_id)
        const profilesById = {}
        for (const a of occAttendees) if (a.profiles) profilesById[a.user_id] = a.profiles
        return {
          id:          ev.id,
          occursAt:    date,
          occurrenceDate: dateStr,
          title:       exception?.override_title || ev.name,
          description: ev.description,
          // Postgres `time` columns come back as "HH:MM:SS" — trim to "HH:MM"
          // for display (matches the slice BibleApp.jsx already does for the
          // Church Settings event list).
          time:        String(exception?.override_time || ev.event_time || '').slice(0, 5),
          location:    exception?.override_location || ev.location,
          recurring:   ev.recurrence,
          repeatUntil: ev.repeat_until,
          hasException: !!exception,
          goingIds:    attendeeIds,
          goingProfilesById: profilesById,
          goingCount:  attendeeIds.length,
          going:       user ? attendeeIds.includes(user.id) : false,
        }
      }).filter(Boolean)
    }).sort((a, b) => a.occursAt - b.occursAt)
    setEvents(rows)
    setLoading(false)
  }, [churchId, user])

  useEffect(() => { fetch() }, [fetch])

  async function toggleGoing(eventId, occurrenceDate, currentlyGoing) {
    if (!user) return { error: 'Sign in to RSVP.' }
    // Snapshot for a revert — previously this only called fetch() on error,
    // which is async and easy to lose track of (e.g. a stale RLS policy
    // silently rejecting the insert/delete left the optimistic "Going ✓"
    // showing correctly in the moment, but reverted next time anything
    // refetched the list — "doesn't stick", with the actual failure only
    // ever logged to the console). Revert immediately and surface the error.
    const prevEvents = events
    setEvents(curr => curr.map(e => (e.id !== eventId || e.occurrenceDate !== occurrenceDate) ? e : ({
      ...e,
      going: !currentlyGoing,
      goingCount: Math.max(0, e.goingCount + (currentlyGoing ? -1 : 1)),
      goingIds: currentlyGoing ? e.goingIds.filter(id => id !== user.id) : [...e.goingIds, user.id],
      goingProfilesById: currentlyGoing
        ? e.goingProfilesById
        : { ...e.goingProfilesById, [user.id]: { full_name: profile?.full_name, avatar_url: profile?.avatar_url } },
    })))
    if (currentlyGoing) {
      const { error } = await supabase.from('event_attendees')
        .delete().eq('event_id', eventId).eq('user_id', user.id).eq('occurrence_date', occurrenceDate)
      if (error) { console.warn('toggleGoing un-RSVP:', error.message); setEvents(prevEvents); return { error: error.message } }
    } else {
      const { error } = await supabase.from('event_attendees')
        .insert({ event_id: eventId, user_id: user.id, occurrence_date: occurrenceDate })
      if (error) { console.warn('toggleGoing RSVP:', error.message); setEvents(prevEvents); return { error: error.message } }
    }
    return { error: null }
  }

  return { events, loading, error, toggleGoing, refresh: fetch }
}
